import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import api from "../../../api/axios";
import {
  bulkCreateMillingSlots,
  updateMillingSlot,
  deleteMillingSlot,
  toggleMillingSlotStatus,
} from "../../../slice/milling/millingSlotSlice";
import WeeklySchedule from "./WeeklySchedule";

/* AdminUser.role is a number; only role 1 is unbound from a single store, and
   only role 1 may prune. The router enforces it — the page mirrors it so a
   scoped admin is not shown a button that always 403s. */
const SUPERADMIN_ROLE = 1;

/* the one literal amber the modals warn in */
const WARN = "#EA580C";

/* ── The flour and masala mills run separate slot schedules ──
   Both pills are alpha washes over whatever surface the theme paints, and the
   flour label rides --mly-heading so it stays legible in the dark palette. */
const MILL_TABS = [
  { key: "flour",  label: "Flour Mill",  color: "var(--mly-heading)", bg: "rgba(26,84,64,0.14)",  border: "rgba(26,84,64,0.45)" },
  { key: "masala", label: "Masala Mill", color: WARN,                 bg: "rgba(234,88,12,0.12)", border: "rgba(234,88,12,0.40)" },
];

const MODAL_LABEL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-muted)", marginBottom: 8, textTransform: "uppercase",
};

const MODAL_INPUT = {
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
  color: "var(--mly-text)", fontSize: 14, boxSizing: "border-box", outline: "none",
};

const MINI_LABEL = {
  fontSize: 10, fontWeight: 700, color: "var(--mly-text-muted)",
  textTransform: "uppercase", letterSpacing: 0.8,
};

/* Semantic reds keep a literal hue in both themes, but the WASHES behind them
   are alpha so they tint whatever surface is underneath instead of painting a
   pale pink block onto the dark theme. */
const DANGER      = "#EF4444";
const DANGER_WASH = "rgba(239,68,68,0.10)";
const DANGER_HOT  = "rgba(239,68,68,0.20)";
const DANGER_EDGE = "rgba(239,68,68,0.35)";

const ERROR_BOX = {
  background: DANGER_WASH, border: `1px solid ${DANGER_EDGE}`, color: DANGER,
  padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
};

/* the store picker, matched to the scope pills on Store/Settings.jsx */
const SCOPE_PILL = (active) => ({
  display: "inline-flex", alignItems: "center", gap: 7,
  padding: "7px 14px", borderRadius: 999, cursor: "pointer",
  border: `1.5px solid ${active ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
  background: active ? "var(--milliyo-forest)" : "var(--mly-surface)",
  color: active ? "#E8C96A" : "var(--mly-text-soft)",
  fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
});

/* ── Utilisation — the one thing this screen exists to show ──
   Green while the window still has room, amber once it is filling, red when
   it is full. The hues are literal because they are semantic; the washes and
   tracks around them come from the theme so both palettes stay readable. */
const AMBER_FROM = 0.7;

const UTIL_FREE = { key: "free", label: "Room",    color: "#22C55E", wash: "rgba(34,197,94,0.10)",  edge: "rgba(34,197,94,0.40)" };
const UTIL_BUSY = { key: "busy", label: "Filling", color: "#F59E0B", wash: "rgba(245,158,11,0.12)", edge: "rgba(245,158,11,0.45)" };
const UTIL_FULL = { key: "full", label: "Full",    color: "#EF4444", wash: "rgba(239,68,68,0.12)",  edge: "rgba(239,68,68,0.45)" };

const utilisationOf = (capacityGrams, reservedGrams) => {
  const cap  = Number(capacityGrams) || 0;
  const used = Number(reservedGrams) || 0;
  const ratio = cap > 0 ? used / cap : (used > 0 ? 1 : 0);
  const level = ratio >= 1 ? UTIL_FULL : ratio >= AMBER_FROM ? UTIL_BUSY : UTIL_FREE;
  return { cap, used, ratio, level, pct: Math.round(ratio * 100) };
};

/* ── Grid geometry ── */
const GRID_DAYS = 7;
const LIST_LIMIT = 10;
const DAY_SHORT   = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* /slots pages over every window ever created, oldest first, and takes no date
   filter, so the grid walks pages until it has passed the end of the week it
   needs. Bounded: past a couple of years of history the walk gives up rather
   than crawl the whole collection.
   The API clamps limit to 1..200 (Math.Clamp in MillingAdminEndpoints.cs), so
   asking for more does not widen the walk — it only makes this constant lie
   about how far RANGE_MAX_PAGES pages actually reach. */
const RANGE_LIMIT = 200;
const RANGE_MAX_PAGES = 12;

const STEP_BTN = {
  width: 34, height: 34, borderRadius: 10, lineHeight: 1,
  border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
  color: "var(--mly-heading)", fontSize: 18, fontWeight: 800, cursor: "pointer",
};

/* ── Helpers ── */
const pad2 = (n) => String(n).padStart(2, "0");

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
};

/* datetime → value usable by <input type="datetime-local"> (local time) */
const toInputDateTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays    = (d, n) => { const x = startOfDay(d); x.setDate(x.getDate() + n); return x; };

/* the calendar keys the grid buckets by — local wall clock, same convention
   the table above has always formatted with */
const dayKey  = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const clockOf = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const slotDay  = (slot) => dayKey(new Date(slot.startsAt));
const slotTime = (slot) => `${clockOf(new Date(slot.startsAt))}-${clockOf(new Date(slot.endsAt))}`;

const shortDate = (d) => `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;

const kgNum = (grams) => `${+((grams || 0) / 1000).toFixed(2)}`;
const toKg  = (grams) => `${kgNum(grams)} kg`;

const qs = (params) =>
  Object.entries(params)
    .filter(([, v]) => v !== "" && v !== null && v !== undefined)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

const EMPTY_WINDOW = { startTime: "", endTime: "", capacityKg: "", leadMin: "90" };

const focusAccent = (e) => { e.target.style.borderColor = "var(--mly-accent)"; };
const blurBorder  = (e) => { e.target.style.borderColor = "var(--mly-border)"; };

/* ── Mill type pill selector (used in both modals) ── */
const MillTypePicker = ({ value, onChange, disabled }) => (
  <div style={{ display: "flex", gap: 10 }}>
    {MILL_TABS.map(({ key, label, color, bg }) => (
      <div key={key}
        onClick={() => { if (!disabled) onChange(key); }}
        style={{
          flex: 1, padding: "10px 0", borderRadius: 10, textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700,
          border:     value === key ? `2px solid ${color}` : "1.5px solid var(--mly-border)",
          background: value === key ? bg                   : "var(--mly-surface-2)",
          color:      value === key ? color                : "var(--mly-text-muted)",
          opacity: disabled && value !== key ? 0.45 : 1,
          transition: "all 0.15s", userSelect: "none",
        }}>
        {label}
      </div>
    ))}
  </div>
);

/* ── The utilisation bar. Track from the theme, fill from the status hue. ── */
const UtilBar = ({ ratio, color, height = 6 }) => (
  <div style={{
    height, borderRadius: 999, background: "var(--mly-surface-3)",
    overflow: "hidden", width: "100%",
  }}>
    <div style={{
      height: "100%", borderRadius: 999, background: color,
      width: `${Math.max(ratio > 0 ? 4 : 0, Math.min(100, Math.round(ratio * 100)))}%`,
      transition: "width 0.2s",
    }} />
  </div>
);

/* what the API refused to delete, whichever shape it reports it in. The route
   answers one entry PER KEPT WINDOW, so a big clean-up can report hundreds —
   the list below is capped rather than poured into the DOM. */
const SKIP_LIST_MAX = 8;

const readSkipReasons = (body) => {
  const raw = body.skippedReasons || body.reasons || body.skipReasons ||
    (Array.isArray(body.skipped) ? body.skipped : null);
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((r) => (typeof r === "string"
      ? { reason: r, label: "", count: null }
      : {
          reason: r.reason || r.why || "Kept",
          label: r.label || "",
          count: r.count ?? null,
        }));
  }
  if (typeof raw === "object") {
    return Object.entries(raw).map(([reason, count]) => ({ reason, label: "", count: Number(count) || 0 }));
  }
  return [];
};

/* Mirrors the `limit 5000` candidate scan in the DELETE /admin/milling/slots/prune
   handler (ledger-service/Api/MillingAdminEndpoints.cs). One
   pass never walks the whole history, so a run that scanned this many rows has
   almost certainly left more behind — the result has to say "run it again"
   rather than read like the calendar is now clean. */
const PRUNE_SCAN_LIMIT = 5000;

/* An estate-wide prune reaches every branch at once and cannot be undone, so
   it is not armed by a click: the operator types this out first. A prune
   pinned to one store asks only for a confirming click. */
const ESTATE_PHRASE = "ALL STORES";

/* the instant a prune with this age would cut at, so the confirmation can name
   a date instead of leaving the operator to do the arithmetic */
const cutoffFrom = (days) => new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000);

/* Mirrors the clamp in the prune handler (MillingAdminEndpoints.cs):
     var days = (int)Math.Clamp(olderThanDays, 0, 3650);
   Past this the server quietly cuts at 3650 days instead, which is NEWER than
   the date the panel would have named — so it would delete everything between
   the two. The screen refuses the age instead of describing one delete and
   firing another. */
const PRUNE_DAYS_MAX = 3650;

/* how many days of slots one press of Generate materializes; the API clamps
   to 1..30 and the input mirrors that */
const APPLY_DAYS_MAX = 30;

const MillingSlots = () => {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.adminUser.role);
  const isSuperAdmin = Number(role) === SUPERADMIN_ROLE;

  const tableRef = useRef(null);
  /* the grid walks several pages per load — a stale walk must never land on
     top of a newer one */
  const reqRef   = useRef(0);
  /* Closures ride their own counter rather than sharing reqRef: the two reads
     start together but finish independently, and a shared counter would have
     each cancel the other. Clicking store A then store B leaves both A's and
     B's closure reads in flight, and they need not come back in order — only
     the newest may write, or A's closed days end up striking out B's week. */
  const closureReqRef = useRef(0);

  const [view,         setView]         = useState("grid");
  const [millType,     setMillType]     = useState("flour");
  const [showSchedule, setShowSchedule] = useState(false);
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [page,         setPage]         = useState(1);

  /* the rows currently on screen — a date window in grid view, one page in
     list view. Reads live here rather than in the slice because only a local
     call can carry the store scope the picker sets; every WRITE still goes
     through the existing thunks. */
  const [slots,      setSlots]      = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [reloadKey,  setReloadKey]  = useState(0);
  /* the range walk ran out of pages before it reached the week — the grid is
     showing part of it, and an empty cell no longer means "no window" */
  const [partial,    setPartial]    = useState(false);

  const [closures, setClosures] = useState([]);

  /* store scope — hidden entirely on a single-store estate.
     EVERY store this account may see, deactivated ones included. The picker
     and any create/generate target below is filtered to the ACTIVE rows, but
     the question "would an unscoped run reach past the store on screen?" has
     to be asked of the whole estate: storeScopeFilter(null) is the EMPTY
     filter, so an unscoped prune reaches deactivated branches too, and GET
     /slots takes no status filter either, so their windows are already on
     this grid. */
  const [allStores, setAllStores] = useState([]);
  const [storeId,   setStoreId]   = useState(null);
  /* Did the store list actually arrive? An empty list means two very
     different things — "one store, nothing to pick" and "the store call
     failed" — and nothing below may read the second as the first. */
  const [storesLoaded, setStoresLoaded] = useState(false);
  /* …and "not yet" is a third thing. Every SCOPE decision still reads an
     unloaded list as the whole estate — in flight or failed, this screen
     cannot narrow what it cannot see — but the two cosmetic surfaces that
     would otherwise flash on every page load (the banner and the per-window
     store badge) wait for the call to settle first. */
  const [storesSettled, setStoresSettled] = useState(false);

  /* the 7 days on screen */
  const [weekStartMs, setWeekStartMs] = useState(() => startOfDay(new Date()).getTime());

  /* ── day-wise add modal ── */
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [addMillType,   setAddMillType]   = useState("flour");
  const [date,          setDate]          = useState("");
  const [repeatForDays, setRepeatForDays] = useState("1");
  const [windows,       setWindows]       = useState([{ ...EMPTY_WINDOW }]);

  /* ── single-slot edit modal ── */
  const [showEditModal,      setShowEditModal]      = useState(false);
  const [editItem,           setEditItem]           = useState(null);
  const [editMillType,       setEditMillType]       = useState("flour");
  const [label,              setLabel]              = useState("");
  const [startsAt,           setStartsAt]           = useState("");
  const [endsAt,             setEndsAt]             = useState("");
  const [capacityKg,         setCapacityKg]         = useState("");
  const [millingLeadMinutes, setMillingLeadMinutes] = useState("90");

  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState("");

  /* ── generate slots from the weekly pattern ── */
  const [applyDays,   setApplyDays]   = useState("7");
  const [applying,    setApplying]    = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  /* Generating across the estate creates bookable capacity in branches this
     screen is not showing, so it gets the same "read it first" step the prune
     has. A generate pinned to one store still goes on one click. */
  const [applyArmed,  setApplyArmed]  = useState(false);

  /* ── past-slot cleanup ── */
  const [pruneDays,   setPruneDays]   = useState("30");
  const [pruneBooked, setPruneBooked] = useState(false);
  const [pruning,     setPruning]     = useState(false);
  const [pruneResult, setPruneResult] = useState(null);
  /* the two-step confirmation: `armed` opens the panel that spells the
     operation out, `typed` holds the phrase an estate-wide prune demands */
  const [pruneArmed,  setPruneArmed]  = useState(false);
  const [pruneTyped,  setPruneTyped]  = useState("");

  const activeTab  = MILL_TABS.find((t) => t.key === millType);

  /* The branches a CREATE or a GENERATE may target. resolveStoreId() on the
     API rejects a store that is closed down, so an inactive branch is never
     offered as a target — but it stays in `allStores` above so its windows
     can still be NAMED on the grid. */
  const stores = useMemo(
    () => allStores.filter((s) => s.status !== false),
    [allStores]
  );

  const activeStore = allStores.find((s) => String(s.storeId) === String(storeId));

  /* Which store an unscoped CREATE lands on. Store.defaultStoreId() carries no
     status filter, so the default store answers even while deactivated — this
     has to read the same list the API does or the modal's note is a guess. */
  const defaultStore   = allStores.find((s) => s.isDefault);
  const defaultStoreId = defaultStore?.storeId || null;
  const createStoreId  = storeId || defaultStoreId;

  /* ── How far an UNSCOPED operation reaches ──────────────────────────────
     `storeId === null` does not mean "this store" — on the API it means no
     store filter at all. Three separate facts decide whether that is safe to
     describe as one store, and reading any of them off the ACTIVE store count
     alone is how an estate-wide delete happens by accident:

       • Only role 1 is ever unscoped. applyStoreIds() and slotStoreScope()
         pin every other role to its own store, so for them "no storeId" is
         already one store.
       • The estate is one calendar only when the WHOLE list — deactivated
         rows included — holds at most one store. Zero rows is a database the
         multi-store migration never ran on: one legacy calendar, which is
         what {} matches there.
       • None of that is known until the list actually loads. A failed load
         narrows nothing; it only removes this screen's ability to NAME the
         branches it is about to reach into, so it reads as estate-wide. */
  const singleStoreEstate = storesLoaded && allStores.length <= 1;
  const ambiguousEstate   = isSuperAdmin && !singleStoreEstate;
  const estateWide        = ambiguousEstate && !storeId;

  const weekStart = useMemo(() => new Date(weekStartMs), [weekStartMs]);
  const weekEnd   = useMemo(() => addDays(weekStart, GRID_DAYS), [weekStart]);
  const days      = useMemo(
    () => Array.from({ length: GRID_DAYS }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const todayKey = dayKey(new Date());

  const refresh = () => setReloadKey((k) => k + 1);

  /* ── reads ────────────────────────────────────────────────────────────
     GET /admin/milling/slots takes (storeId, millType, upcoming, page, limit)
     and nothing else — there is no date filter to hint at the window with, so
     the page walk below is the only thing that finds the week. */
  const fetchRange = useCallback(async (from, to, seq) => {
    const out = [];
    /* did the walk actually get past the end of the window, or did it stop
       because it ran out of allowed pages? An empty cell only means "no
       window here" in the first case. */
    let reached = false;
    for (let p = 1; p <= RANGE_MAX_PAGES; p++) {
      const res = await api.get(`/admin/milling/slots?${qs({
        page: p, limit: RANGE_LIMIT, millType,
        storeId: storeId || "",
      })}`);
      /* the operator has already moved to another store or another week —
         stop paging rather than spend a dozen more requests fetching rows
         that load() is going to throw away anyway */
      if (seq !== undefined && seq !== reqRef.current) return { rows: [], truncated: false };
      const rows = res.data?.data || [];
      rows.forEach((s) => {
        const t = new Date(s.startsAt).getTime();
        if (t >= from.getTime() && t < to.getTime()) out.push(s);
      });
      const totalPages = Number(res.data?.pagination?.totalPages) || 1;
      if (!rows.length || p >= totalPages) { reached = true; break; }
      /* ascending order — once a page ends past the window, nothing later
         can fall inside it */
      if (new Date(rows[rows.length - 1].startsAt).getTime() >= to.getTime()) {
        reached = true;
        break;
      }
    }
    return { rows: out, truncated: !reached };
  }, [millType, storeId]);

  const load = useCallback(async () => {
    const seq = ++reqRef.current;
    setLoading(true); setError("");
    try {
      let rows = [];
      let pages = {};
      let cut = false;
      if (view === "grid") {
        const range = await fetchRange(weekStart, weekEnd, seq);
        rows = range.rows;
        cut  = range.truncated;
      } else {
        const res = await api.get(`/admin/milling/slots?${qs({
          page, limit: LIST_LIMIT, millType,
          upcoming: upcomingOnly ? "true" : "",
          storeId: storeId || "",
        })}`);
        rows  = res.data?.data || [];
        pages = res.data?.pagination || {};
      }
      if (seq !== reqRef.current) return;
      setSlots(rows);
      setPagination(pages);
      setPartial(cut);
    } catch (err) {
      if (seq !== reqRef.current) return;
      setSlots([]);
      setPartial(false);
      setError(err.response?.data?.message || "Failed to fetch slots");
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [view, page, upcomingOnly, millType, storeId, weekStart, weekEnd, fetchRange]);

  useEffect(() => { load(); }, [load, reloadKey]);

  /* ── closures ──
     GET /closures is never estate-wide: oneStoreScope() answers a superadmin
     that names no store with the DEFAULT store's closures, not everyone's.
     That is right for the editor it feeds, but the grid above IS showing every
     branch at once in "All stores" mode, so one unscoped read would leave
     another branch's festival unmarked while painting the default store's
     across the whole row. Ask each reachable store in turn instead, and tag
     every row with the store it came back from. */
  const loadClosures = useCallback(async () => {
    const seq = closureReqRef.current + 1;
    closureReqRef.current = seq;

    /* Estate-wide the grid mixes branches, so ask each one this account may
       name. Deactivated branches are not asked: oneStoreScope() answers a
       named inactive store with an error, so they can only be left unmarked. */
    const targets = estateWide && stores.length > 0
      ? stores.map((s) => s.storeId)
      : [storeId || ""];

    try {
      /* one branch answering badly must not blank every OTHER branch's closed
         days — a failed leg drops out and the rest still mark the grid */
      const replies = await Promise.all(
        targets.map((id) =>
          api
            .get(`/admin/milling/closures?${qs({ millType, storeId: id || "" })}`)
            .catch(() => null)
        )
      );
      if (seq !== closureReqRef.current) return;
      setClosures(
        replies.flatMap((res, i) =>
          !res ? [] : (res.data?.data || []).map((c) => ({
            ...c,
            /* rows carry their own storeId now; a legacy row written before
               the migration carries none, so fall back to the store the API
               says it answered for and finally to the one we asked about */
            storeId: c.storeId || res.data?.storeId || targets[i] || null,
          }))
        )
      );
    } catch {
      if (seq !== closureReqRef.current) return;
      setClosures([]);
    }
  }, [millType, storeId, stores, estateWide]);

  useEffect(() => { loadClosures(); }, [loadClosures, reloadKey]);

  /* a single-store estate never sees the picker; an account bound to one
     store is answered with only that store, so the check covers both.

     GET /admin/stores answers the whole estate for a superadmin and its own
     store for anyone else, and sending no ?status= returns the deactivated
     branches too — which is what this screen needs, since an unscoped prune
     reaches them and the grid already shows their windows.

     It is also the one read on this screen whose envelope goes: the handler
     answers Results.Json(rows), so res.data IS the array. Reading res.data.data
     here would yield an empty picker with no error anywhere. */
  useEffect(() => {
    let alive = true;
    api.get("/admin/stores")
      .then((res) => {
        if (!alive) return;
        /* keep the deactivated rows: an unscoped prune reaches them and the
           grid already shows their windows, so they must stay nameable */
        const rows = Array.isArray(res.data)
          ? res.data.map((s) => ({
              storeId: String(s.id),
              name: s.name || "Unnamed store",
              isDefault: !!s.isDefault,
              status: s.isActive !== false,
              /* TODO(cutover): server does not return hasMill — core.MstStores
                 has no has_mill column, and POST /admin/milling/schedule/apply
                 now fills EVERY active store rather than the milling ones. Held
                 as null = unknown, never guessed: false would pin a "no mill"
                 badge on every branch and refuse to generate anywhere, true
                 would assert a fact the server has no data for. Every reader
                 below tests `=== false`, so unknown claims nothing. */
              hasMill: null,
            }))
          : [];
        setAllStores(rows);
        setStoresLoaded(true);
        setStoresSettled(true);
        /* Exactly one store in the WHOLE estate: pin to it and show no picker,
           so every read and write names the store outright instead of relying
           on the API's fallback. Counting only the active rows here is what
           used to leave storeId null on a two-store estate whose other branch
           happened to be switched off — and a null storeId is the estate.

           Only when that one store is the DEFAULT one, and still open.
           storeScopeFilter() widens the default store to { $in: [id, null] }
           and so still sees rows written before the multi-store migration;
           pinning to a non-default store would filter on its id alone and make
           those legacy windows vanish from a screen that shows them today. In
           that case stay unpinned — with a single store in the estate the
           unscoped read IS that store's calendar, exactly as it has always
           been, and singleStoreEstate above says so honestly. */
        if (rows.length === 1 && rows[0].isDefault && rows[0].status) {
          setStoreId(rows[0].storeId);
        }
      })
      .catch(() => {
        if (!alive) return;
        setAllStores([]);
        setStoresLoaded(false);
        setStoresSettled(true);
      });
    return () => { alive = false; };
  }, []);

  /* Name the branch a row belongs to.
     A row with NO storeId predates the multi-store migration and genuinely
     belongs to the default store — storeScopeFilter() folds it in there. An id
     that is not in the list is something this screen cannot identify, and on a
     screen whose whole job is telling branches apart, saying "Default store"
     for it would attribute one branch's window to a specific, wrong one. The
     lookup runs over ALL stores so a deactivated branch is named rather than
     mistaken for the default. */
  const storeNameOf = (id) => {
    if (!id) return defaultStore?.name || "Default store";
    const hit = allStores.find((s) => String(s.storeId) === String(id));
    if (!hit) return "Unknown store";
    return hit.status === false ? `${hit.name} (inactive)` : hit.name;
  };

  /* ── grid buckets ── */
  const timeRows = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const key = slotTime(s);
      if (!map.has(key)) {
        map.set(key, {
          key,
          startTime: clockOf(new Date(s.startsAt)),
          endTime: clockOf(new Date(s.endsAt)),
        });
      }
    });
    return [...map.values()].sort(
      (a, b) => a.startTime.localeCompare(b.startTime) || a.endTime.localeCompare(b.endTime)
    );
  }, [slots]);

  const cells = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const key = `${slotDay(s)}|${slotTime(s)}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return map;
  }, [slots]);

  const dayTotals = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const key = slotDay(s);
      const acc = map.get(key) || { cap: 0, used: 0, count: 0 };
      acc.cap  += Number(s.capacityGrams) || 0;
      acc.used += Number(s.reservedGrams) || 0;
      acc.count += 1;
      map.set(key, acc);
    });
    return map;
  }, [slots]);

  /* ── closures, per calendar date ──
     A scoped read only ever returns the one store's closures, so every row it
     answers with shuts the day being looked at. Estate-wide a superadmin gets
     every branch's, and only the store a new window would land on may strike
     the day out — otherwise one branch's festival would block creating
     windows for all the others. */
  const closureByDate = useMemo(() => {
    /* Mirrors exactly the branch loadClosures took. Only the per-store fan-out
       returns rows from several branches; every other path made ONE read whose
       rows all belong to the store a new window would land on — including the
       fallback when the store list never arrived, where the unscoped read and
       the unscoped create both resolve to the default store. Reading that case
       as "several branches" would leave a genuinely closed day unmarked. */
    const scoped = !(estateWide && stores.length > 0);
    const grouped = new Map();
    closures.forEach((c) => {
      if (!c?.closedOn) return;
      const key = String(c.closedOn);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(c);
    });

    const map = new Map();
    grouped.forEach((rows, key) => {
      const blocking = scoped || rows.some((c) => {
        const owner = c.storeId ? String(c.storeId) : String(defaultStoreId || "");
        return owner === String(createStoreId || "");
      });
      map.set(key, {
        blocking,
        names: scoped ? "" : [...new Set(rows.map((c) => storeNameOf(c.storeId)))].join(", "),
        reason: [...new Set(rows.map((c) => c.reason).filter(Boolean))].join("; "),
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closures, estateWide, stores, storeId, createStoreId, defaultStoreId, allStores]);

  /* ── DataTable — list view only; ordering off so the server's date-ascending
       order (and the visual date grouping below) survives ── */

  /* ── add modal handlers ── */
  const openAddModal = (prefill = {}) => {
    setAddMillType(millType);
    setDate(prefill.date || "");
    setRepeatForDays("1");
    setWindows([{
      ...EMPTY_WINDOW,
      startTime: prefill.startTime || "",
      endTime:   prefill.endTime || "",
    }]);
    setFormError(""); setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false); setDate(""); setRepeatForDays("1");
    setWindows([{ ...EMPTY_WINDOW }]); setFormError("");
  };

  const addWindowRow = () => setWindows((prev) => [...prev, { ...EMPTY_WINDOW }]);

  const removeWindowRow = (idx) => setWindows((prev) => prev.filter((_, i) => i !== idx));

  const updateWindowRow = (idx, field, value) =>
    setWindows((prev) => prev.map((w, i) => (i === idx ? { ...w, [field]: value } : w)));

  const handleBulkSave = async () => {
    if (!date) { setFormError("Date is required"); return; }
    const repeatDays = Number(repeatForDays);
    if (repeatForDays === "" || isNaN(repeatDays) || repeatDays < 1 || repeatDays > 30 || !Number.isInteger(repeatDays)) {
      setFormError("Repeat for days must be a whole number between 1 and 30"); return;
    }
    for (let i = 0; i < windows.length; i++) {
      const w = windows[i];
      if (!w.startTime || !w.endTime) { setFormError(`Window ${i + 1}: start and end times are required`); return; }
      if (w.endTime <= w.startTime) { setFormError(`Window ${i + 1}: end time must be after start time`); return; }
      if (w.capacityKg === "" || isNaN(w.capacityKg) || Number(w.capacityKg) <= 0) { setFormError(`Window ${i + 1}: valid capacity (kg) is required`); return; }
      if (w.leadMin === "" || isNaN(w.leadMin) || Number(w.leadMin) < 0) { setFormError(`Window ${i + 1}: valid lead minutes is required`); return; }
    }

    setSaving(true); setFormError("");

    const data = {
      millType: addMillType,
      date,
      windows: windows.map((w) => ({
        startTime: w.startTime,
        endTime: w.endTime,
        capacityGrams: Math.round(Number(w.capacityKg) * 1000),
        millingLeadMinutes: Number(w.leadMin),
      })),
      repeatForDays: repeatDays,
    };
    /* the API reads the target store from `store`; without one it falls back
       to the default store, which is what a single-store estate wants */
    if (storeId) data.store = storeId;

    try {
      const result = await dispatch(bulkCreateMillingSlots(data)).unwrap();
      toast.success(result.message || "Slots created successfully!");
      setMillType(addMillType);
      setPage(1);
      refresh();
      closeAddModal();
    } catch (err) {
      setFormError(err || "Something went wrong");
      toast.error(err || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  /* ── edit modal handlers ── */
  const openEditModal = (item) => {
    setEditItem(item);
    setEditMillType(item.millType || "flour");
    setLabel(item.label);
    setStartsAt(toInputDateTime(item.startsAt));
    setEndsAt(toInputDateTime(item.endsAt));
    setCapacityKg(item.capacityGrams ? kgNum(item.capacityGrams) : "");
    setMillingLeadMinutes(String(item.leadMinutes ?? 90));
    setFormError(""); setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false); setEditItem(null);
    setEditMillType("flour"); setLabel(""); setStartsAt(""); setEndsAt("");
    setCapacityKg(""); setMillingLeadMinutes("90"); setFormError("");
  };

  const handleUpdate = async () => {
    if (!label.trim()) { setFormError("Slot label is required"); return; }
    if (!startsAt)     { setFormError("Start date & time is required"); return; }
    if (!endsAt)       { setFormError("End date & time is required"); return; }
    if (new Date(endsAt) <= new Date(startsAt)) { setFormError("End time must be after start time"); return; }
    if (capacityKg === "" || isNaN(capacityKg) || Number(capacityKg) <= 0) { setFormError("Valid capacity (kg) is required"); return; }
    if (millingLeadMinutes === "" || isNaN(millingLeadMinutes) || Number(millingLeadMinutes) < 0) { setFormError("Valid lead minutes is required"); return; }

    setSaving(true); setFormError("");

    const data = {
      label: label.trim(),
      millType: editMillType,
      startsAt: new Date(startsAt).toISOString(),
      endsAt: new Date(endsAt).toISOString(),
      capacityGrams: Math.round(Number(capacityKg) * 1000),
      millingLeadMinutes: Number(millingLeadMinutes),
    };

    try {
      /* the slot id is a bigint now and PUT /admin/milling/slots/{id:long} is
         route-constrained: a 24-hex id matches no route and comes back as a
         bodiless 404, i.e. a toast with nothing in it */
      await dispatch(updateMillingSlot({ id: editItem.id, data })).unwrap();
      toast.success("Slot updated successfully!");
      refresh();
      closeEditModal();
    } catch (err) {
      setFormError(err || "Something went wrong");
      toast.error(err || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this slot?")) return;
    try {
      await dispatch(deleteMillingSlot(id)).unwrap();
      /* the row the edit modal is sitting on is gone — close it rather than
         leave a form pointed at nothing */
      if (editItem && editItem.id === id) closeEditModal();
      refresh();
      toast.success("Slot deleted successfully!");
    } catch (err) {
      toast.error(err || "Delete failed");
    }
  };

  const handleToggle = async (id) => {
    try {
      await dispatch(toggleMillingSlotStatus(id)).unwrap();
      refresh();
    } catch (err) {
      toast.error(err || "Failed to update status");
    }
  };

  /* ── generate slots from the weekly pattern ──
     POST /schedule/apply answers with a perStore breakdown because a
     superadmin that names no store fills EVERY milling store at once. Showing
     only the total would hide the fact that one branch produced nothing —
     usually because it has no template saved for this mill.

     The scope reading is the same one the prune uses, storesLoaded included: a
     generate is not destructive, but it creates BOOKABLE capacity that
     customers can order into, and doing that across branches this screen just
     called "this store" is not a mistake anyone can see afterwards. */
  const applyScopeLabel = storeId
    ? (activeStore?.name || "the selected store")
    : estateWide ? "every active store" : "this store";

  /* TODO(cutover): server does not return hasMill, so this pre-flight has
     nothing behind it any more. ApplyTargetsAsync targets every `is_active`
     store — there is no has_mill column to narrow on — so an apply no longer
     refuses a branch that sources its milling elsewhere. Kept, but testing
     `=== false`, so an unknown stays quiet instead of blocking every generate. */
  const scopedStoreCantMill = !!storeId && !!activeStore && activeStore.hasMill === false;
  /* estate-wide, these branches simply will not appear in the perStore
     breakdown — the readout has to explain the gap rather than leave rows
     missing against a picker full of pills. Empty while hasMill is unknown. */
  const nonMillingStores = stores.filter((s) => s.hasMill === false);

  /* ── Which days a generate actually fills ──
     The route starts its horizon at IST today unless it is handed fromDate, so
     a control sitting directly under a calendar the operator has paged forward
     would quietly fill a different week than the empty one on screen — and
     with days capped at 30, a week further out than that could never be filled
     from here at all. Start at the week being looked at, but never behind
     today: windows already in the past are skipped server-side, so a horizon
     that begins before today just burns its days. */
  const applyFrom = useMemo(() => {
    const today = startOfDay(new Date());
    return view === "grid" && weekStart.getTime() > today.getTime() ? weekStart : today;
  }, [view, weekStart]);
  const applyFromKey  = dayKey(applyFrom);
  const applyDaysNum  = Number(applyDays);
  const applyDaysValid =
    applyDays !== "" && !isNaN(applyDaysNum) && Number.isInteger(applyDaysNum) &&
    applyDaysNum >= 1 && applyDaysNum <= APPLY_DAYS_MAX;
  const applyTo    = applyDaysValid ? addDays(applyFrom, applyDaysNum - 1) : null;
  const applyAhead = applyFromKey !== todayKey;

  const handleApply = async () => {
    if (!applyDaysValid) {
      toast.error(`Days must be a whole number between 1 and ${APPLY_DAYS_MAX}`);
      return;
    }
    if (scopedStoreCantMill) {
      toast.error(`${activeStore?.name || "This branch"} sources its milling from another store — there is no calendar here to generate`);
      return;
    }

    setApplying(true); setApplyResult(null);
    /* the store rides in the query AND the body: the route reads ?storeId
       first, then body.storeId, then body.store, and naming it in both means a
       stray proxy dropping one does not silently widen a one-branch generate
       into an estate-wide one */
    const payload = { millType, days: applyDaysNum, fromDate: applyFromKey };
    if (storeId) payload.storeId = storeId;
    const url = storeId
      ? `/admin/milling/schedule/apply?storeId=${encodeURIComponent(storeId)}`
      : "/admin/milling/schedule/apply";

    try {
      const res = await api.post(url, payload);
      const body = res.data || {};
      setApplyResult({
        message: body.message || "",
        created: Array.isArray(body.data) ? body.data.length : 0,
        perStore: Array.isArray(body.perStore) ? body.perStore : [],
        /* the run describes itself: which mill, which scope, which days. The
           response echoes none of the three, and a result that cannot say what
           it was reads as the answer to whatever the header says now. */
        millLabel: activeTab.label,
        scope: applyScopeLabel,
        estate: estateWide,
        from: applyFrom,
        to: applyTo,
      });
      toast.success(body.message || "Slots generated from the weekly schedule");
      refresh();
    } catch (err) {
      const msg = err.response?.data?.message || "Couldn't generate slots from the weekly schedule";
      setApplyResult({ failed: msg });
      toast.error(msg);
    } finally {
      setApplying(false);
      setApplyArmed(false);
    }
  };

  /* One click generates a store's own calendar; an estate-wide run opens the
     panel below first, exactly as the prune does. */
  const requestApply = () => {
    if (!applyDaysValid) {
      toast.error(`Days must be a whole number between 1 and ${APPLY_DAYS_MAX}`);
      return;
    }
    if (scopedStoreCantMill) {
      toast.error(`${activeStore?.name || "This branch"} sources its milling from another store — there is no calendar here to generate`);
      return;
    }
    if (estateWide) {
      setApplyResult(null);
      setApplyArmed(true);
      return;
    }
    handleApply();
  };

  /* ── past-slot cleanup ──
     Deleting rows is the one irreversible thing on this screen, so the
     confirmation spells out its exact reach: which mill, which store, which
     cutoff date, and whether booked windows are included.

     Without a store pick the API prunes the whole estate — storeScopeFilter()
     answers a null store with the EMPTY filter, which is every branch,
     deactivated ones included. So does a prune fired while the store list is
     unknown: the call failing does not narrow anything, it only stops the
     screen from being able to NAME the branches it is about to reach into.
     Both read as estate-wide in `estateWide` above; guessing the narrow
     reading is exactly how an estate-wide delete happens by accident. */
  const pruneStoreLabel = storeId
    ? (activeStore?.name || "the selected store")
    : estateWide ? "EVERY store" : "this store";

  /* An age past the server's own clamp would have this panel name one cutoff
     and the route cut at a completely different — and much NEWER — one, so the
     delete would reach years further back than the words the operator agreed
     to. Refuse it rather than silently clamp: someone typing 20000 means
     "effectively never", not "ten years". */
  const pruneDaysNum = Number(pruneDays);
  const pruneDaysValid =
    pruneDays !== "" && !isNaN(pruneDaysNum) && Number.isInteger(pruneDaysNum) &&
    pruneDaysNum >= 1 && pruneDaysNum <= PRUNE_DAYS_MAX;

  const pruneDaysMessage =
    `Older than must be a whole number of days between 1 and ${PRUNE_DAYS_MAX.toLocaleString("en-IN")}` +
    ` — the clean-up never cuts further back than that`;

  const pruneCutoff = pruneDaysValid ? cutoffFrom(pruneDays) : null;

  /* Any change to what the prune WOULD do disarms the confirmation, so the
     panel's words can never describe one operation while the button fires
     another — retyping the phrase is the cost of changing your mind. The store
     list is in here too: it can land after the panel is open and turn a reading
     of "this store" into "EVERY store". */
  useEffect(() => {
    setPruneArmed(false);
    setPruneTyped("");
  }, [pruneDays, pruneBooked, millType, storeId, allStores, storesLoaded]);

  /* A finished run's numbers belong to the mill and the store it ran on, and
     the response echoes neither the mill nor a name this screen can re-derive
     — so leaving the box up under a header that now says a different mill or a
     different branch reads as that other calendar having been cleaned. It has
     not been. Same for the generate breakdown. */
  useEffect(() => {
    setPruneResult(null);
    setApplyResult(null);
  }, [millType, storeId, allStores, storesLoaded]);

  /* the generate confirmation disarms on anything that changes what it would
     do — the horizon and the day count included, since the panel names both */
  useEffect(() => {
    setApplyArmed(false);
  }, [millType, storeId, allStores, storesLoaded, applyDays, applyFromKey]);

  const armPrune = () => {
    if (!pruneDaysValid) {
      toast.error(pruneDaysMessage);
      return;
    }
    setPruneResult(null);
    setPruneTyped("");
    setPruneArmed(true);
  };

  const handlePrune = async () => {
    if (!pruneDaysValid) {
      toast.error(pruneDaysMessage);
      return;
    }
    const olderThanDays = pruneDaysNum;
    if (estateWide && pruneTyped.trim().toUpperCase() !== ESTATE_PHRASE) {
      toast.error(`Type ${ESTATE_PHRASE} to confirm a clean-up across every store`);
      return;
    }

    setPruning(true); setPruneResult(null);
    /* the same fields ride as query params and as a body — a DELETE body is
       not read everywhere, and either shape reaches the API (the route merges
       both and lets the body win). millType rides along so the clean-up
       reaches no further than the tab it was pressed on: the other mill's
       history is not this button's business. Carrying storeId twice matters
       most of all — a dropped body must not turn this store's clean-up into
       the estate's. */
    const payload = {
      olderThanDays,
      includeBooked: pruneBooked,
      millType,
    };
    if (storeId) payload.storeId = storeId;

    try {
      const res = await api.delete("/admin/milling/slots/prune", { params: payload, data: payload });
      /* this route answers with the payload itself — there is no data wrapper
         to unpick, unlike the /slots and /closures reads above */
      const body = res.data || {};
      const skipped = Array.isArray(body.skipped) ? body.skipped.length : Number(body.skipped) || 0;
      const scanned = Number(body.scanned) || 0;
      setPruneResult({
        deleted: Number(body.deleted) || 0,
        skipped,
        reasons: readSkipReasons(body),
        keptBooked: !pruneBooked,
        scanned,
        /* the cutoff the API actually worked out, not the one this screen
           predicted — they agree, and saying the server's answer is what
           makes the reading evidence rather than a restatement */
        cutoff: body.cutoff || null,
        /* the route echoes the scope it acted on; null means the whole estate */
        scopedTo: body.storeId || null,
        /* the response never says WHICH mill it cleaned, so the run has to
           carry that itself — "412 deleted" under a header naming the other
           mill would read as that mill's history being gone */
        millLabel: activeTab.label,
        /* one pass is capped, so a full scan means there is more still there */
        more: scanned >= PRUNE_SCAN_LIMIT,
      });
      toast.success(body.message || `${Number(body.deleted) || 0} slot(s) removed`);
      setPruneArmed(false);
      setPruneTyped("");
      refresh();
    } catch (err) {
      /* the refusal is a real 403 whose body is { code: "forbidden" } and
         carries no message, so the status is the only thing that can name it */
      const msg = err.response?.data?.message
        || (err.response?.status === 403
          ? "Only a super admin may clean up past slots"
          : "Couldn't clean up past slots");
      setPruneResult({ failed: msg });
      toast.error(msg);
    } finally {
      setPruning(false);
    }
  };

  /* ── grid pieces ── */
  const renderSlotBlock = (item) => {
    const { cap, used, ratio, level, pct } = utilisationOf(item.capacityGrams, item.reservedGrams);
    const off = item.isActive === false;
    return (
      <div key={item.id}
        onClick={() => openEditModal(item)}
        title={`${item.label} — ${toKg(used)} of ${toKg(cap)} booked${off ? " (slot is off)" : ""}`}
        style={{
          background: level.wash, border: `1px solid ${level.edge}`,
          borderLeft: `3px solid ${level.color}`,
          borderRadius: 9, padding: "7px 8px 8px", cursor: "pointer",
          display: "flex", flexDirection: "column", gap: 5,
          opacity: off ? 0.55 : 1, transition: "transform 0.12s, box-shadow 0.12s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "var(--mly-shadow-sm)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "none";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mly-text)", whiteSpace: "nowrap" }}>
            {clockOf(new Date(item.startsAt))}–{clockOf(new Date(item.endsAt))}
          </span>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: level.color }}>{pct}%</span>
        </div>

        <UtilBar ratio={ratio} color={level.color} />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
          <span style={{ fontSize: 10.5, color: "var(--mly-text-soft)", fontWeight: 600 }}>
            {kgNum(used)} / {kgNum(cap)} kg
          </span>
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, color: level.color, textTransform: "uppercase" }}>
            {off ? "Off" : level.label}
          </span>
        </div>

        {/* Estate-wide the grid is showing several branches' windows mixed
            together and clicking one edits THAT branch's capacity —
            findSlotInScope resolves it with no scope at all. Say whose it is.
            `estateWide` covers the failed-store-list case too, where every
            badge honestly reads "Unknown store". */}
        {estateWide && storesSettled && (
          <span style={{
            alignSelf: "flex-start", fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
            padding: "1px 6px", borderRadius: 999, textTransform: "uppercase",
            background: "var(--mly-surface-2)", color: "var(--mly-text-muted)",
            border: "1px solid var(--mly-border)",
          }}>
            {storeNameOf(item.storeId)}
          </span>
        )}
      </div>
    );
  };

  const renderEmptyCell = (day, row, closed) => (
    <button
      type="button"
      disabled={!!closed}
      onClick={() => openAddModal({ date: dayKey(day), startTime: row?.startTime, endTime: row?.endTime })}
      title={closed ? `Mill closed${closed.reason ? ` — ${closed.reason}` : ""}` : "Add a window here"}
      style={{
        width: "100%", minHeight: 58, borderRadius: 9,
        border: `1px dashed ${closed ? DANGER_EDGE : "var(--mly-border)"}`,
        background: "transparent", color: "var(--mly-text-muted)",
        cursor: closed ? "not-allowed" : "pointer",
        fontSize: 16, fontWeight: 700, lineHeight: 1,
        opacity: closed ? 0.5 : 0.55, transition: "opacity 0.12s, background 0.12s",
      }}
      onMouseEnter={(e) => {
        if (closed) return;
        e.currentTarget.style.opacity = "1";
        e.currentTarget.style.background = "var(--mly-surface-2)";
      }}
      onMouseLeave={(e) => {
        if (closed) return;
        e.currentTarget.style.opacity = "0.55";
        e.currentTarget.style.background = "transparent";
      }}
    >
      {closed ? "—" : "+"}
    </button>
  );

  const renderGrid = () => (
    <>
      {/* stepper + legend */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, marginBottom: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setWeekStartMs(addDays(weekStart, -GRID_DAYS).getTime())}
            title="Previous 7 days"
            style={STEP_BTN}>‹</button>
          <div style={{
            minWidth: 210, textAlign: "center", fontSize: 14, fontWeight: 800,
            color: "var(--mly-heading)",
          }}>
            {shortDate(days[0])} – {shortDate(days[GRID_DAYS - 1])} {days[GRID_DAYS - 1].getFullYear()}
          </div>
          <button onClick={() => setWeekStartMs(addDays(weekStart, GRID_DAYS).getTime())}
            title="Next 7 days"
            style={STEP_BTN}>›</button>
          <button onClick={() => setWeekStartMs(startOfDay(new Date()).getTime())}
            style={{
              marginLeft: 4, padding: "7px 14px", borderRadius: 999,
              border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
              color: "var(--mly-text-soft)", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}>
            Today
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          {[UTIL_FREE, UTIL_BUSY, UTIL_FULL].map((l) => (
            <span key={l.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--mly-text-soft)", fontWeight: 600 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: l.color, display: "inline-block" }} />
              {l.key === "busy" ? `Filling (${Math.round(AMBER_FROM * 100)}%+)` : l.label}
            </span>
          ))}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--mly-text-soft)", fontWeight: 600 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: DANGER_WASH, border: `1px solid ${DANGER_EDGE}`, display: "inline-block" }} />
            Closed
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4" style={{ color: "var(--mly-text-soft)" }}>Loading...</div>
      ) : error ? (
        <div style={ERROR_BOX}>{error}</div>
      ) : (
        <>
          {partial && (
            <div style={{
              background: UTIL_BUSY.wash, border: `1px solid ${UTIL_BUSY.edge}`,
              color: "var(--mly-text)", padding: "10px 14px", borderRadius: 8,
              marginBottom: 12, fontSize: 12.5,
            }}>
              There is more past history than this view can page through, so some of
              this week&apos;s windows may be missing — an empty cell here is not proof
              the window is gone. Clean up past slots below, then reload.
            </div>
          )}
          <div style={{ overflowX: "auto", paddingBottom: 4 }}>
            <div style={{
              minWidth: 940,
              display: "grid",
              gridTemplateColumns: `76px repeat(${GRID_DAYS}, minmax(118px, 1fr))`,
              gap: 8, alignItems: "stretch",
            }}>
              {/* corner */}
              <div />

              {/* day headers */}
              {days.map((d) => {
                const key    = dayKey(d);
                const info   = closureByDate.get(key);
                const closed = info?.blocking ? info : null;
                const totals = dayTotals.get(key);
                const util   = totals ? utilisationOf(totals.cap, totals.used) : null;
                const isToday = key === todayKey;
                return (
                  <div key={key} style={{
                    background: closed ? DANGER_WASH : isToday ? "var(--mly-accent-soft)" : "var(--mly-surface-2)",
                    border: `1px solid ${closed ? DANGER_EDGE : isToday ? "var(--mly-accent)" : "var(--mly-border)"}`,
                    borderRadius: 10, padding: "8px 10px 9px",
                    display: "flex", flexDirection: "column", gap: 5,
                  }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
                      <span style={{
                        fontSize: 12.5, fontWeight: 800, color: "var(--mly-heading)",
                        textDecoration: closed ? "line-through" : "none",
                      }}>
                        {DAY_SHORT[d.getDay()]}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: "var(--mly-text-muted)",
                        textDecoration: closed ? "line-through" : "none",
                      }}>
                        {shortDate(d)}
                      </span>
                    </div>

                    {info && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: DANGER, lineHeight: 1.3 }}>
                        Closed{info.names ? ` — ${info.names}` : ""}
                        {info.reason ? ` (${info.reason})` : ""}
                      </span>
                    )}

                    {util && totals.count ? (
                      <>
                        <UtilBar ratio={util.ratio} color={util.level.color} height={5} />
                        <span style={{ fontSize: 10, color: "var(--mly-text-soft)", fontWeight: 600 }}>
                          {kgNum(totals.used)} / {kgNum(totals.cap)} kg · {totals.count} slot{totals.count === 1 ? "" : "s"}
                        </span>
                      </>
                    ) : info ? null : (
                      <span style={{ fontSize: 10, color: "var(--mly-text-muted)", fontStyle: "italic" }}>
                        No windows
                      </span>
                    )}
                  </div>
                );
              })}

              {/* time rows */}
              {(timeRows.length ? timeRows : [null]).map((row, rowIdx) => (
                <React.Fragment key={row ? row.key : `blank-${rowIdx}`}>
                  <div style={{
                    display: "flex", flexDirection: "column", justifyContent: "center",
                    padding: "6px 4px", borderRight: "1px solid var(--mly-border-soft)",
                  }}>
                    {row ? (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "var(--mly-text)", fontVariantNumeric: "tabular-nums" }}>
                          {row.startTime}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--mly-text-muted)", fontVariantNumeric: "tabular-nums" }}>
                          {row.endTime}
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: 10.5, color: "var(--mly-text-muted)", fontStyle: "italic" }}>
                        No windows
                      </span>
                    )}
                  </div>

                  {days.map((d) => {
                    const key    = dayKey(d);
                    const info   = closureByDate.get(key);
                    const closed = info?.blocking ? info : null;
                    const inCell = row ? (cells.get(`${key}|${row.key}`) || []) : [];
                    return (
                      <div key={`${key}|${row ? row.key : rowIdx}`} style={{
                        display: "flex", flexDirection: "column", gap: 6,
                        padding: 2, borderRadius: 10,
                        background: closed ? DANGER_WASH : "transparent",
                      }}>
                        {inCell.length
                          ? inCell.map(renderSlotBlock)
                          : renderEmptyCell(d, row, closed)}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}

      {!loading && !error && (
        <div style={{ fontSize: 11.5, color: "var(--mly-text-muted)", marginTop: 12 }}>
          Click a window to edit it, or an empty cell to add one on that day and time.
          Rows are the windows this mill actually runs in the days shown.
        </div>
      )}
    </>
  );

  const renderList = () => (
    <>
      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : error ? (
        <div className="text-danger text-center py-4">{error}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="display table table-bordered" ref={tableRef} style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Si No</th>
                <th>Date</th>
                <th>Label</th>
                <th>Window</th>
                <th>Capacity</th>
                <th>Reserved</th>
                <th>Remaining</th>
                <th>Lead (min)</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {slots.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: "40px 0", color: "var(--mly-text-muted)" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
                        fill="none" stroke="var(--mly-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mly-text-muted)" }}>No {activeTab.label.toLowerCase()} slots found</span>
                      <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                        {upcomingOnly ? "No upcoming slots — try switching off the filter" : "Add slots from the week grid, or from the weekly schedule"}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                slots.map((item, index) => {
                  const prev = index > 0 ? slots[index - 1] : null;
                  const isNewDate = !prev || formatDate(prev.startsAt) !== formatDate(item.startsAt);
                  const remaining = (item.capacityGrams || 0) - (item.reservedGrams || 0);
                  return (
                    <tr key={item.id}>
                      <td>{(page - 1) * LIST_LIMIT + index + 1}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {isNewDate ? (
                          <span style={{ background: activeTab.bg, color: activeTab.color, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700, border: `1px solid ${activeTab.border}` }}>
                            {formatDate(item.startsAt)}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>{formatDate(item.startsAt)}</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{item.label}</td>
                      <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {formatTime(item.startsAt)} – {formatTime(item.endsAt)}
                      </td>
                      <td>{toKg(item.capacityGrams)}</td>
                      <td>
                        <span style={{ background: UTIL_BUSY.wash, color: UTIL_BUSY.color, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700, border: `1px solid ${UTIL_BUSY.edge}` }}>
                          {toKg(item.reservedGrams)}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: remaining > 0 ? UTIL_FREE.wash : UTIL_FULL.wash,
                          color:      remaining > 0 ? UTIL_FREE.color : UTIL_FULL.color,
                          borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700,
                          border: `1px solid ${remaining > 0 ? UTIL_FREE.edge : UTIL_FULL.edge}`,
                        }}>
                          {toKg(remaining)}
                        </span>
                      </td>
                      <td>{item.leadMinutes}</td>
                      <td>
                        <label className="switch">
                          <input type="checkbox" checked={!!item.isActive} onChange={() => handleToggle(item.id)} />
                          <span className="slider round" />
                        </label>
                      </td>
                      <td className="d-flex gap-2">
                        <button onClick={() => openEditModal(item)} title="Edit"
                          style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "var(--mly-surface-2)", color: "var(--mly-heading)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "var(--mly-surface-3)"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "var(--mly-surface-2)"}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m16.6 2.9 4.5 4.5-11 11a1.6 1.6 0 0 1-.8.4l-5 1a1 1 0 0 1-1.2-1.2l1-5a1.6 1.6 0 0 1 .4-.8Zm5.9 3.1-4.5-4.5.8-.8a2.2 2.2 0 0 1 3.1 0l1.4 1.4a2.2 2.2 0 0 1 0 3.1Z" /></svg>
                        </button>
                        <button onClick={() => handleDelete(item.id)} title="Delete"
                          style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: DANGER_WASH, color: DANGER, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = DANGER_HOT}
                          onMouseLeave={(e) => e.currentTarget.style.background = DANGER_WASH}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.6 2.2h4.8a1.6 1.6 0 0 1 1.6 1.6v1.4h4.6a1 1 0 0 1 0 2h-.5l-1 12.4a2.4 2.4 0 0 1-2.4 2.2H7.3a2.4 2.4 0 0 1-2.4-2.2l-1-12.4h-.5a1 1 0 0 1 0-2h4.6V3.8a1.6 1.6 0 0 1 1.6-1.6Zm.4 3h4V4.2h-4Zm-.1 5a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Zm4.2 0a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Z" /></svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          {Array.from({ length: pagination.totalPages }, (_, i) => (
            <button key={i + 1}
              className={`btn btn-sm ${page === i + 1 ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setPage(i + 1)}
            >{i + 1}</button>
          ))}
          <button className="btn btn-sm btn-outline-secondary" disabled={page === pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </>
  );

  return (
    <div>
      <div className="container-fluid">
        {/* Day-wise weekly templates → concrete slots. Collapsed by default so
            the week grid is what the screen opens on; the closures editor
            lives inside it. */}
        <div style={{ marginBottom: showSchedule ? 0 : 16 }}>
          <button onClick={() => setShowSchedule((p) => !p)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "9px 16px", borderRadius: 10,
              border: "1.5px solid var(--mly-border)",
              background: showSchedule ? "var(--mly-accent-soft)" : "var(--mly-surface)",
              color: "var(--mly-heading)", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>
            <span style={{ fontSize: 11 }}>{showSchedule ? "▾" : "▸"}</span>
            Weekly schedule &amp; closures
          </button>
          {ambiguousEstate && (
            /* The editor below sends no storeId, and GET/PUT /schedule answer a
               superadmin that names none with the DEFAULT store — so it edits
               that store's pattern no matter which pill is lit. Say which store
               that is by name, and warn harder once the two disagree. */
            <div style={{
              fontSize: 11, marginTop: 6,
              color: storeId && String(storeId) !== String(defaultStoreId)
                ? WARN : "var(--mly-text-muted)",
              fontWeight: storeId && String(storeId) !== String(defaultStoreId) ? 700 : 400,
            }}>
              The recurring pattern below is not store-scoped yet — it reads and writes{" "}
              <strong>{defaultStore?.name || "the default store"}</strong>&apos;s schedule
              whichever store is picked below.
              {storeId && String(storeId) !== String(defaultStoreId) && (
                <> Editing it will not change {activeStore?.name || "the selected store"}.</>
              )}
            </div>
          )}
        </div>

        {showSchedule && (
          <WeeklySchedule millType={millType} onGenerated={refresh} />
        )}

        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>

            {/* Mill type tabs */}
            <div style={{ display: "flex", gap: 10 }}>
              {MILL_TABS.map((tab) => {
                const isActive = millType === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => { setMillType(tab.key); setPage(1); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "9px 18px",
                      borderRadius: 30,
                      border: isActive ? `1.5px solid ${tab.color}` : "1.5px solid var(--mly-border)",
                      background: isActive ? tab.bg : "var(--mly-surface)",
                      color: isActive ? tab.color : "var(--mly-text-muted)",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 13.5,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {view === "list" && (
                <button
                  onClick={() => { setUpcomingOnly((p) => !p); setPage(1); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "9px 18px", borderRadius: 30,
                    border: upcomingOnly ? "1.5px solid var(--mly-heading)" : "1.5px solid var(--mly-border)",
                    background: upcomingOnly ? "var(--mly-accent-soft)" : "var(--mly-surface)",
                    color: upcomingOnly ? "var(--mly-heading)" : "var(--mly-text-muted)",
                    fontWeight: upcomingOnly ? 700 : 500,
                    fontSize: 13.5, cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  Upcoming Only
                  {upcomingOnly && <span style={{ fontSize: 14 }}>✓</span>}
                </button>
              )}

              {/* view toggle — the table stays available for anyone who
                  prefers reading the numbers */}
              <div style={{
                display: "flex", padding: 3, gap: 3, borderRadius: 30,
                background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
              }}>
                {[
                  { key: "grid", label: "Week grid" },
                  { key: "list", label: "List" },
                ].map((v) => {
                  const isActive = view === v.key;
                  return (
                    <button key={v.key}
                      onClick={() => { setView(v.key); setPage(1); }}
                      style={{
                        padding: "6px 16px", borderRadius: 30, border: "none",
                        background: isActive ? "var(--mly-heading)" : "transparent",
                        color: isActive ? "var(--mly-on-accent)" : "var(--mly-text-muted)",
                        fontWeight: isActive ? 700 : 600, fontSize: 13,
                        cursor: "pointer", transition: "all 0.15s",
                      }}>
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="card-body">
            {/* The store list is what lets this screen scope anything and name
                anything. Without it every read below is the whole estate and
                every branch reads "Unknown store" — say that once, at the top,
                rather than leaving the badges to be puzzled over. */}
            {isSuperAdmin && storesSettled && !storesLoaded && (
              <div style={{
                background: UTIL_BUSY.wash, border: `1px solid ${UTIL_BUSY.edge}`,
                color: "var(--mly-text)", padding: "10px 14px", borderRadius: 8,
                marginBottom: 16, fontSize: 12.5,
              }}>
                The store list didn&apos;t load, so this screen can&apos;t tell the branches
                apart or scope anything to one of them. Everything below reads and writes
                across <strong>every store</strong> — reload before generating or cleaning up.
              </div>
            )}

            {/* ── Store scope — hidden entirely on a single-store estate ── */}
            {ambiguousEstate && stores.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                  <button type="button" onClick={() => { setStoreId(null); setPage(1); }} style={SCOPE_PILL(!storeId)}>
                    All stores
                  </button>
                  {stores.map((s) => {
                    const active = String(s.storeId) === String(storeId);
                    return (
                      <button key={s.storeId} type="button"
                        onClick={() => { setStoreId(s.storeId); setPage(1); }}
                        style={SCOPE_PILL(active)}>
                        {s.name}
                        {/* a branch that sources its milling elsewhere can be
                            read and cleaned up, but never generated into.
                            TODO(cutover): server does not return hasMill, so
                            this never renders — see the store mapping above. */}
                        {s.hasMill === false && (
                          <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>
                            · no mill
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>
                  {storeId ? (
                    <>
                      Showing the <strong style={{ color: "var(--mly-text-soft)" }}>{activeStore?.name || "selected store"}</strong> mill
                      calendar — new windows are created for it.
                      {scopedStoreCantMill && (
                        <span style={{ color: WARN, fontWeight: 700 }}>
                          {" "}This branch mills nothing in-house — it sources from another store,
                          so &ldquo;Generate slots&rdquo; has no calendar here to fill.
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      Every store&apos;s windows at once, this estate&apos;s deactivated branches
                      included. New windows created here go to{" "}
                      <strong style={{ color: "var(--mly-text-soft)" }}>{storeNameOf(createStoreId)}</strong> —
                      pick a store first to target another.
                    </>
                  )}
                </div>
              </div>
            )}

            {view === "grid" ? renderGrid() : renderList()}

            {/* ── Fill the calendar from the weekly pattern ──
                 Slots materialize on their own (on save, and daily), so this
                 is the manual nudge for a store whose horizon needs extending
                 now. It is the one call that answers per store, and that
                 answer is the point: estate-wide it says which branches
                 actually produced windows and which quietly produced none. */}
            <div style={{
              marginTop: 24, padding: "14px 16px", borderRadius: 12,
              background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
            }}>
              <div style={{ ...MINI_LABEL, color: "var(--mly-text-soft)", marginBottom: 10 }}>
                Generate {activeTab.label.toLowerCase()} slots from the weekly schedule — {applyScopeLabel}
              </div>

              <div style={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ ...MINI_LABEL, marginBottom: 4 }}>Days ahead</div>
                  <input type="number" value={applyDays} min="1" max={APPLY_DAYS_MAX} step="1"
                    onChange={(e) => setApplyDays(e.target.value)}
                    style={{
                      ...MODAL_INPUT, width: 130, padding: "9px 12px",
                      borderColor: applyDaysValid ? "var(--mly-border)" : DANGER,
                    }}
                    onFocus={focusAccent} onBlur={blurBorder}
                  />
                  {!applyDaysValid && (
                    <div style={{ fontSize: 11, color: DANGER, marginTop: 4, maxWidth: 260 }}>
                      Days must be a whole number between 1 and {APPLY_DAYS_MAX}.
                    </div>
                  )}
                </div>

                <button onClick={requestApply} disabled={applying || applyArmed || scopedStoreCantMill}
                  style={{
                    padding: "11px 22px", borderRadius: 10,
                    border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
                    color: "var(--mly-heading)", fontWeight: 800, fontSize: 13,
                    cursor: applying || applyArmed || scopedStoreCantMill ? "not-allowed" : "pointer",
                    opacity: applying || applyArmed || scopedStoreCantMill ? 0.6 : 1,
                  }}>
                  {applying ? "Generating..." : estateWide ? "Generate slots…" : "Generate slots"}
                </button>
              </div>

              <div style={{ fontSize: 11.5, color: "var(--mly-text-muted)", marginTop: 8 }}>
                Materializes the saved weekly windows for{" "}
                <strong style={{ color: "var(--mly-text-soft)" }}>{applyScopeLabel}</strong>
                {applyTo ? (
                  <>
                    {" "}across{" "}
                    <strong style={{ color: "var(--mly-text-soft)", fontVariantNumeric: "tabular-nums" }}>
                      {formatDate(applyFrom)} – {formatDate(applyTo)}
                    </strong>
                    {applyAhead && <> — the week the grid is showing</>}
                  </>
                ) : null}.
                Nothing is overwritten — windows that already exist are skipped, closed days
                and windows already in the past are never created.
                {!applyAhead && view === "grid" && (
                  <> The horizon starts today; page the calendar forward to fill a later week.</>
                )}
              </div>

              {/* ── The estate-wide generate, spelled out ──
                   Same reason the prune has a panel: this writes bookable
                   capacity into branches that are not on screen, and the words
                   here have to match what the button fires. ── */}
              {applyArmed && (
                <div style={{
                  marginTop: 12, padding: "12px 14px", borderRadius: 10,
                  background: "rgba(234,88,12,0.12)", border: `1.5px solid rgba(234,88,12,0.40)`,
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: WARN, marginBottom: 8 }}>
                    This generates across EVERY store that mills in-house
                  </div>

                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--mly-text)" }}>
                    <li>Mill: <strong>{activeTab.label}</strong> only.</li>
                    {/* TODO(cutover): server does not return hasMill, and
                        ApplyTargetsAsync fills every ACTIVE store rather than
                        only the in-house mills — so the reach named here is
                        every active branch, not a milling subset of them. */}
                    <li>
                      Scope: <strong>every active branch</strong>
                      {!storesLoaded && (
                        <> — the store list did not load, so this screen cannot name
                        the branches it would reach</>
                      )}
                      {storesLoaded && stores.length > 0 && (
                        <> ({stores.map((s) => s.name).join(", ")})</>
                      )}
                    </li>
                    <li>
                      Days:{" "}
                      <strong style={{ fontVariantNumeric: "tabular-nums" }}>
                        {applyTo ? `${formatDate(applyFrom)} – ${formatDate(applyTo)}` : "-"}
                      </strong>{" "}
                      ({applyDaysNum} day{applyDaysNum === 1 ? "" : "s"}).
                    </li>
                    <li>
                      New windows become bookable by customers as soon as they exist.
                      Nothing existing is overwritten or removed.
                    </li>
                  </ul>

                  <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                    <button onClick={() => setApplyArmed(false)} disabled={applying}
                      style={{
                        padding: "9px 18px", borderRadius: 10,
                        border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
                        color: "var(--mly-text-soft)", fontWeight: 700, fontSize: 12.5,
                        cursor: applying ? "not-allowed" : "pointer",
                      }}>
                      Cancel
                    </button>
                    <button onClick={handleApply} disabled={applying}
                      style={{
                        padding: "9px 18px", borderRadius: 10, border: "none",
                        background: WARN, color: "#fff", fontWeight: 800, fontSize: 12.5,
                        cursor: applying ? "not-allowed" : "pointer",
                        opacity: applying ? 0.5 : 1,
                      }}>
                      {applying
                        ? "Generating..."
                        : `Generate ${activeTab.label.toLowerCase()} windows in every active store`}
                    </button>
                  </div>
                </div>
              )}

              {applyResult && (
                <div style={{
                  marginTop: 12, padding: "10px 14px", borderRadius: 10,
                  background: applyResult.failed ? DANGER_WASH : "var(--mly-surface)",
                  border: `1px solid ${applyResult.failed ? DANGER_EDGE : "var(--mly-border)"}`,
                  fontSize: 12.5, color: applyResult.failed ? DANGER : "var(--mly-text-soft)",
                }}>
                  {applyResult.failed ? applyResult.failed : (
                    <>
                      <div style={{ fontWeight: 700, color: "var(--mly-text)" }}>
                        {applyResult.message || `${applyResult.created} slot(s) created`}
                      </div>

                      {/* the run in its own words — nothing in the response says
                          which mill or which days it was, so it says so itself */}
                      <div style={{ marginTop: 4, color: "var(--mly-text-muted)", fontSize: 11.5 }}>
                        {applyResult.millLabel} · {applyResult.scope}
                        {applyResult.to && (
                          <> · <span style={{ fontVariantNumeric: "tabular-nums" }}>
                            {formatDate(applyResult.from)} – {formatDate(applyResult.to)}
                          </span></>
                        )}
                      </div>

                      {applyResult.perStore.length > 0 && (
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                          {applyResult.perStore.map((s, i) => (
                            <div key={s.storeId ? String(s.storeId) : `row-${i}`}
                              style={{
                                display: "flex", alignItems: "baseline", justifyContent: "space-between",
                                gap: 10, maxWidth: 420,
                              }}>
                              <span style={{ color: "var(--mly-text)" }}>
                                {s.storeName || storeNameOf(s.storeId)}
                              </span>
                              <span style={{
                                fontVariantNumeric: "tabular-nums", fontWeight: 700,
                                color: Number(s.created) > 0 ? UTIL_FREE.color : "var(--mly-text-muted)",
                              }}>
                                {Number(s.created) || 0} slot{Number(s.created) === 1 ? "" : "s"}
                              </span>
                            </div>
                          ))}
                          {applyResult.perStore.some((s) => !Number(s.created)) && (
                            <div style={{ marginTop: 4, color: "var(--mly-text-muted)" }}>
                              A branch listed on zero has no active weekly template for this
                              mill, or its windows for those days already exist, or every day
                              in the range is a closed day for it.
                            </div>
                          )}
                          {/* A branch with no in-house mill is not "on zero" —
                              it is not in this list at all. Rows missing against
                              a full row of pills need saying, or the operator
                              reads the absence as one of the causes above.
                              TODO(cutover): server does not return hasMill and
                              the apply targets every active store, so this never
                              renders — every active branch is in perStore. */}
                          {applyResult.estate && nonMillingStores.length > 0 && (
                            <div style={{ marginTop: 4, color: "var(--mly-text-muted)" }}>
                              Not listed:{" "}
                              <strong style={{ color: "var(--mly-text-soft)" }}>
                                {nonMillingStores.map((s) => s.name).join(", ")}
                              </strong>{" "}
                              — {nonMillingStores.length === 1 ? "this branch has" : "these branches have"}{" "}
                              no in-house mill and {nonMillingStores.length === 1 ? "sources" : "source"}{" "}
                              milling from another store, so there is no calendar of{" "}
                              {nonMillingStores.length === 1 ? "its" : "their"} own to fill.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Housekeeping: drop the past windows nobody reads any more.
                 The route is superadmin-only, so a store-bound admin is not
                 shown a button that can only answer 403. ── */}
            {isSuperAdmin && (
              <div style={{
                marginTop: 24, padding: "14px 16px", borderRadius: 12,
                background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
              }}>
                <div style={{ ...MINI_LABEL, color: "var(--mly-text-soft)", marginBottom: 10 }}>
                  Clean up past {activeTab.label.toLowerCase()} slots — {pruneStoreLabel}
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ ...MINI_LABEL, marginBottom: 4 }}>Older than (days)</div>
                    <input type="number" value={pruneDays} min="1" max={PRUNE_DAYS_MAX} step="1"
                      onChange={(e) => setPruneDays(e.target.value)}
                      style={{
                        ...MODAL_INPUT, width: 130, padding: "9px 12px",
                        borderColor: pruneDaysValid ? "var(--mly-border)" : DANGER,
                      }}
                      onFocus={focusAccent} onBlur={blurBorder}
                    />
                    {!pruneDaysValid && (
                      <div style={{ fontSize: 11, color: DANGER, marginTop: 4, maxWidth: 260 }}>
                        {pruneDaysMessage}.
                      </div>
                    )}
                  </div>

                  <label style={{
                    display: "flex", alignItems: "center", gap: 8, marginBottom: 0,
                    padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                    border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
                    fontSize: 12.5, fontWeight: 600, color: "var(--mly-text-soft)",
                  }}>
                    <input type="checkbox" checked={pruneBooked}
                      onChange={(e) => setPruneBooked(e.target.checked)} />
                    Include slots that had bookings
                  </label>

                  <button onClick={armPrune} disabled={pruning || pruneArmed || !pruneDaysValid}
                    style={{
                      padding: "11px 22px", borderRadius: 10,
                      border: `1.5px solid ${DANGER_EDGE}`, background: DANGER_WASH,
                      color: DANGER, fontWeight: 800, fontSize: 13,
                      cursor: pruning || pruneArmed || !pruneDaysValid ? "not-allowed" : "pointer",
                      opacity: pruning || pruneArmed || !pruneDaysValid ? 0.6 : 1,
                    }}>
                    {pruning ? "Cleaning..." : "Clean up…"}
                  </button>
                </div>

                <div style={{ fontSize: 11.5, color: "var(--mly-text-muted)", marginTop: 8 }}>
                  Removes <strong style={{ color: "var(--mly-text-soft)" }}>{activeTab.label.toLowerCase()}</strong>{" "}
                  windows in <strong style={{ color: "var(--mly-text-soft)" }}>{pruneStoreLabel}</strong> that
                  ended more than that many days ago — the other mill and any store you
                  have not selected are left alone. Unbooked windows only unless you tick
                  the box; orders keep their own copy of the slot times, so a past order
                  stays readable either way. The clean-up never reaches further back
                  than {PRUNE_DAYS_MAX.toLocaleString("en-IN")} days.
                </div>

                {/* ── The confirmation, spelled out ──
                     Not a browser dialog: the operator has to be able to read
                     the cutoff as a DATE and the scope as a NAME before
                     agreeing, and an estate-wide reach has to cost more than
                     one reflex click. Any edit to the fields above closes this
                     panel, so what it describes is always what will run. */}
                {pruneArmed && (
                  <div style={{
                    marginTop: 12, padding: "12px 14px", borderRadius: 10,
                    background: estateWide ? DANGER_HOT : DANGER_WASH,
                    border: `1.5px solid ${DANGER_EDGE}`,
                  }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: DANGER, marginBottom: 8 }}>
                      {estateWide
                        ? "This deletes across EVERY store — read it before confirming"
                        : "Confirm this clean-up"}
                    </div>

                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--mly-text)" }}>
                      <li>
                        Mill: <strong>{activeTab.label}</strong> only — the other mill is untouched.
                      </li>
                      <li>
                        Scope: <strong>{pruneStoreLabel}</strong>
                        {estateWide && !storesLoaded && (
                          <> — the store list did not load, so this screen cannot name the
                          branches it would reach</>
                        )}
                        {/* deactivated branches are named too: the empty store
                            filter reaches their calendars just the same */}
                        {estateWide && storesLoaded && allStores.length > 0 && (
                          <> ({allStores.map((s) => (s.status === false ? `${s.name} (inactive)` : s.name)).join(", ")})</>
                        )}
                      </li>
                      <li>
                        Deletes windows that ended before{" "}
                        <strong style={{ fontVariantNumeric: "tabular-nums" }}>
                          {pruneCutoff ? `${formatDate(pruneCutoff)} ${formatTime(pruneCutoff)}` : "-"}
                        </strong>{" "}
                        ({pruneDays} day{Number(pruneDays) === 1 ? "" : "s"} ago). A window that
                        has not ended yet is never deleted.
                      </li>
                      <li>
                        {pruneBooked
                          ? "Windows that HAD bookings are included — orders keep their own copy of the slot times, and any order that cannot describe itself without the row blocks its delete."
                          : "Unbooked windows only — anything with reserved grams is kept."}
                      </li>
                      <li>This cannot be undone.</li>
                    </ul>

                    {estateWide && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ ...MINI_LABEL, marginBottom: 4, color: DANGER }}>
                          Type {ESTATE_PHRASE} to confirm
                        </div>
                        <input type="text" value={pruneTyped}
                          onChange={(e) => setPruneTyped(e.target.value)}
                          placeholder={ESTATE_PHRASE} autoComplete="off" spellCheck="false"
                          style={{ ...MODAL_INPUT, width: 220, padding: "9px 12px" }}
                          onFocus={focusAccent} onBlur={blurBorder}
                        />
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                      <button onClick={() => { setPruneArmed(false); setPruneTyped(""); }}
                        disabled={pruning}
                        style={{
                          padding: "9px 18px", borderRadius: 10,
                          border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
                          color: "var(--mly-text-soft)", fontWeight: 700, fontSize: 12.5,
                          cursor: pruning ? "not-allowed" : "pointer",
                        }}>
                        Cancel
                      </button>
                      <button onClick={handlePrune}
                        disabled={pruning || (estateWide && pruneTyped.trim().toUpperCase() !== ESTATE_PHRASE)}
                        style={{
                          padding: "9px 18px", borderRadius: 10, border: "none",
                          background: DANGER, color: "#fff", fontWeight: 800, fontSize: 12.5,
                          cursor: pruning || (estateWide && pruneTyped.trim().toUpperCase() !== ESTATE_PHRASE)
                            ? "not-allowed" : "pointer",
                          opacity: pruning || (estateWide && pruneTyped.trim().toUpperCase() !== ESTATE_PHRASE)
                            ? 0.5 : 1,
                        }}>
                        {pruning
                          ? "Deleting..."
                          : `Delete past ${activeTab.label.toLowerCase()} windows in ${pruneStoreLabel}`}
                      </button>
                    </div>
                  </div>
                )}

                {pruneResult && (
                  <div style={{
                    marginTop: 12, padding: "10px 14px", borderRadius: 10,
                    background: pruneResult.failed ? DANGER_WASH : "var(--mly-surface)",
                    border: `1px solid ${pruneResult.failed ? DANGER_EDGE : "var(--mly-border)"}`,
                    fontSize: 12.5, color: pruneResult.failed ? DANGER : "var(--mly-text-soft)",
                  }}>
                    {pruneResult.failed ? pruneResult.failed : (
                      <>
                        <div style={{ fontWeight: 700, color: "var(--mly-text)", fontVariantNumeric: "tabular-nums" }}>
                          {pruneResult.deleted} deleted · {pruneResult.skipped} kept
                          {pruneResult.scanned > 0 && <> · {pruneResult.scanned} scanned</>}
                        </div>

                        {/* what the API says it actually did, in its own words —
                            the scope it applied and the instant it cut at. The
                            mill comes from the run itself: the response never
                            names one, so without this the numbers would read as
                            whichever mill the tab happens to show. */}
                        <div style={{ marginTop: 4, color: "var(--mly-text-muted)", fontSize: 11.5 }}>
                          {pruneResult.millLabel} ·{" "}
                          {pruneResult.scopedTo
                            ? <>in <strong style={{ color: "var(--mly-text-soft)" }}>{storeNameOf(pruneResult.scopedTo)}</strong></>
                            : <>across <strong style={{ color: DANGER }}>every store</strong></>}
                          {pruneResult.cutoff && (
                            <> · windows that ended before{" "}
                              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                                {formatDate(pruneResult.cutoff)} {formatTime(pruneResult.cutoff)}
                              </span>
                            </>
                          )}
                        </div>

                        {pruneResult.skipped > 0 && (
                          pruneResult.reasons.length ? (
                            <>
                              {/* a kept window was kept for a REASON — a booked
                                  slot, or an order that still needs the row to
                                  describe itself. Show the reason, not a count. */}
                              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                                {pruneResult.reasons.slice(0, SKIP_LIST_MAX).map((r, i) => (
                                  <li key={i}>
                                    {r.label ? `${r.label} — ` : ""}{r.reason}
                                    {r.count != null ? ` — ${r.count}` : ""}
                                  </li>
                                ))}
                              </ul>
                              {pruneResult.reasons.length > SKIP_LIST_MAX && (
                                <div style={{ marginTop: 4 }}>
                                  …and {pruneResult.reasons.length - SKIP_LIST_MAX} more kept for the same kinds of reason.
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ marginTop: 6 }}>
                              {pruneResult.keptBooked
                                ? "Skipped windows still have orders booked against them. Tick “Include slots that had bookings” to remove those too."
                                : "The API kept these windows without giving a reason."}
                            </div>
                          )
                        )}

                        {pruneResult.more && (
                          <div style={{ marginTop: 6, color: WARN, fontWeight: 600 }}>
                            One pass only scans {PRUNE_SCAN_LIMIT.toLocaleString("en-IN")} windows,
                            and this one filled that scan — there is almost certainly more past
                            history left. Run the clean-up again.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── ADD MODAL (day-wise) ── */}
      {showAddModal && (
        <>
          <div onClick={closeAddModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)", zIndex: 1040 }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1050, width: "100%", maxWidth: 640,
            background: "var(--mly-surface)", borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            padding: "32px 32px 28px",
            maxHeight: "90vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>New Slots</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ background: "var(--mly-surface-2)", color: "var(--mly-heading)", fontSize: 11, fontWeight: 700, letterSpacing: 1, padding: "4px 12px", borderRadius: 20, border: "1px solid var(--mly-heading)" }}>
                  ADDING
                </span>
                <button onClick={closeAddModal} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", cursor: "pointer", fontSize: 16, color: "var(--mly-text-soft)" }}>×</button>
              </div>
            </div>

            <hr style={{ borderColor: "var(--mly-border-soft)", marginBottom: 20 }} />

            {formError && <div style={ERROR_BOX}>{formError}</div>}

            {ambiguousEstate && (
              <div style={{ fontSize: 11.5, color: "var(--mly-text-muted)", marginBottom: 16 }}>
                These windows are created for{" "}
                <strong style={{ color: "var(--mly-text-soft)" }}>
                  {storeId
                    ? (activeStore?.name || "the selected store")
                    : storeNameOf(createStoreId)}
                </strong>.
              </div>
            )}

            {/* Mill Type */}
            <div style={{ marginBottom: 16 }}>
              <label style={MODAL_LABEL}>
                Mill Type <span style={{ color: DANGER }}>*</span>
              </label>
              <MillTypePicker value={addMillType} onChange={setAddMillType} />
            </div>

            {/* Date + Repeat */}
            <div className="row" style={{ marginBottom: 16 }}>
              <div className="col-6">
                <label style={MODAL_LABEL}>
                  Date <span style={{ color: DANGER }}>*</span>
                </label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  style={MODAL_INPUT} onFocus={focusAccent} onBlur={blurBorder}
                />
              </div>
              <div className="col-6">
                <label style={MODAL_LABEL}>
                  Repeat For (days) <span style={{ color: DANGER }}>*</span>
                </label>
                <input type="number" value={repeatForDays} onChange={(e) => setRepeatForDays(e.target.value)}
                  placeholder="1" min="1" max="30" step="1"
                  style={MODAL_INPUT} onFocus={focusAccent} onBlur={blurBorder}
                />
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                  Creates the same windows for up to 30 consecutive days. Exact duplicates are skipped.
                </div>
              </div>
            </div>

            {/* Window rows */}
            <div style={{ marginBottom: 16 }}>
              <label style={MODAL_LABEL}>
                Windows <span style={{ color: DANGER }}>*</span>
              </label>

              <div style={{ display: "flex", gap: 8, marginBottom: 6, paddingRight: 44 }}>
                <div style={{ width: 120, flexShrink: 0, ...MINI_LABEL }}>Start</div>
                <div style={{ width: 120, flexShrink: 0, ...MINI_LABEL }}>End</div>
                <div style={{ flex: 1, ...MINI_LABEL }}>Capacity (kg)</div>
                <div style={{ width: 90, flexShrink: 0, ...MINI_LABEL }}>Lead (min)</div>
              </div>

              {windows.map((w, idx) => (
                <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <input type="time" value={w.startTime}
                    onChange={(e) => updateWindowRow(idx, "startTime", e.target.value)}
                    style={{ ...MODAL_INPUT, width: 120, flexShrink: 0, padding: "9px 10px" }}
                    onFocus={focusAccent} onBlur={blurBorder}
                  />
                  <input type="time" value={w.endTime}
                    onChange={(e) => updateWindowRow(idx, "endTime", e.target.value)}
                    style={{ ...MODAL_INPUT, width: 120, flexShrink: 0, padding: "9px 10px" }}
                    onFocus={focusAccent} onBlur={blurBorder}
                  />
                  <input type="number" value={w.capacityKg} placeholder="e.g. 50" min="0" step="0.5"
                    onChange={(e) => updateWindowRow(idx, "capacityKg", e.target.value)}
                    style={{ ...MODAL_INPUT, flex: 1, width: "auto", padding: "9px 10px" }}
                    onFocus={focusAccent} onBlur={blurBorder}
                  />
                  <input type="number" value={w.leadMin} placeholder="90" min="0" step="5"
                    onChange={(e) => updateWindowRow(idx, "leadMin", e.target.value)}
                    style={{ ...MODAL_INPUT, width: 90, flexShrink: 0, padding: "9px 10px" }}
                    onFocus={focusAccent} onBlur={blurBorder}
                  />
                  <button onClick={() => removeWindowRow(idx)} title="Remove window" disabled={windows.length === 1}
                    style={{
                      width: 36, height: 36, borderRadius: 8, border: "none", flexShrink: 0,
                      background: windows.length === 1 ? "var(--mly-surface-2)" : DANGER_WASH,
                      color: windows.length === 1 ? "var(--mly-text-muted)" : DANGER,
                      cursor: windows.length === 1 ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                    onMouseEnter={(e) => { if (windows.length > 1) e.currentTarget.style.background = DANGER_HOT; }}
                    onMouseLeave={(e) => { if (windows.length > 1) e.currentTarget.style.background = DANGER_WASH; }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.6 2.2h4.8a1.6 1.6 0 0 1 1.6 1.6v1.4h4.6a1 1 0 0 1 0 2h-.5l-1 12.4a2.4 2.4 0 0 1-2.4 2.2H7.3a2.4 2.4 0 0 1-2.4-2.2l-1-12.4h-.5a1 1 0 0 1 0-2h4.6V3.8a1.6 1.6 0 0 1 1.6-1.6Zm.4 3h4V4.2h-4Zm-.1 5a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Zm4.2 0a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Z" /></svg>
                  </button>
                </div>
              ))}

              <button onClick={addWindowRow}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px dashed var(--mly-heading)", background: "var(--mly-surface-2)", color: "var(--mly-heading)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                + Add Window
              </button>
              <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 8 }}>
                Slots are auto-labelled from their times (e.g. "10:00 AM - 12:00 PM").
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={closeAddModal} style={{ padding: "11px 24px", borderRadius: 10, border: "1.5px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text-soft)", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleBulkSave} disabled={saving} style={{ flex: 1, padding: "11px 24px", borderRadius: 10, border: "none", background: saving ? "var(--mly-accent)" : "var(--mly-heading)", color: "var(--mly-on-accent)", fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer" }}>
                {saving ? "Saving..." : "Create Slots"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── EDIT MODAL (single slot) ── */}
      {showEditModal && (
        <>
          <div onClick={closeEditModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)", zIndex: 1040 }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1050, width: "100%", maxWidth: 520,
            background: "var(--mly-surface)", borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            padding: "32px 32px 28px",
            maxHeight: "90vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Edit Slot</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ background: "var(--mly-surface-2)", color: "var(--mly-heading)", fontSize: 11, fontWeight: 700, letterSpacing: 1, padding: "4px 12px", borderRadius: 20, border: "1px solid var(--mly-heading)" }}>
                  EDITING
                </span>
                <button onClick={closeEditModal} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", cursor: "pointer", fontSize: 16, color: "var(--mly-text-soft)" }}>×</button>
              </div>
            </div>

            <hr style={{ borderColor: "var(--mly-border-soft)", marginBottom: 20 }} />

            {formError && <div style={ERROR_BOX}>{formError}</div>}

            {/* Utilisation of the slot being edited — the same reading the grid gives */}
            {editItem && (() => {
              const u = utilisationOf(editItem.capacityGrams, editItem.reservedGrams);
              return (
                <div style={{
                  marginBottom: 20, padding: "10px 14px", borderRadius: 10,
                  background: u.level.wash, border: `1px solid ${u.level.edge}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12, fontWeight: 700 }}>
                    <span style={{ color: "var(--mly-text)" }}>
                      {toKg(editItem.reservedGrams)} of {toKg(editItem.capacityGrams)} booked
                    </span>
                    <span style={{ color: u.level.color }}>{u.pct}% · {u.level.label}</span>
                  </div>
                  <UtilBar ratio={u.ratio} color={u.level.color} />
                  {/* estate-wide the grid mixes branches — say whose window this is
                      before anyone edits or deletes it */}
                  {estateWide && (
                    <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 6 }}>
                      {storeNameOf(editItem.storeId)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Mill Type */}
            <div style={{ marginBottom: 16 }}>
              <label style={MODAL_LABEL}>
                Mill Type <span style={{ color: DANGER }}>*</span>
              </label>
              <MillTypePicker
                value={editMillType}
                onChange={setEditMillType}
                disabled={!!editItem && editItem.reservedGrams > 0}
              />
              {editItem && editItem.reservedGrams > 0 && (
                <div style={{ fontSize: 11, color: WARN, marginTop: 4 }}>
                  Mill type can't be changed — this slot already has orders booked
                </div>
              )}
            </div>

            {/* Label */}
            <div style={{ marginBottom: 16 }}>
              <label style={MODAL_LABEL}>
                Slot Label <span style={{ color: DANGER }}>*</span>
              </label>
              <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. 10:00 AM - 12:00 PM"
                style={MODAL_INPUT} onFocus={focusAccent} onBlur={blurBorder}
              />
            </div>

            {/* Starts / Ends */}
            <div className="row" style={{ marginBottom: 16 }}>
              <div className="col-6">
                <label style={MODAL_LABEL}>
                  Starts At <span style={{ color: DANGER }}>*</span>
                </label>
                <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
                  style={MODAL_INPUT} onFocus={focusAccent} onBlur={blurBorder}
                />
              </div>
              <div className="col-6">
                <label style={MODAL_LABEL}>
                  Ends At <span style={{ color: DANGER }}>*</span>
                </label>
                <input type="datetime-local" value={endsAt} min={startsAt || undefined} onChange={(e) => setEndsAt(e.target.value)}
                  style={MODAL_INPUT} onFocus={focusAccent} onBlur={blurBorder}
                />
              </div>
            </div>

            {/* Capacity / Lead */}
            <div className="row" style={{ marginBottom: 24 }}>
              <div className="col-6">
                <label style={MODAL_LABEL}>
                  Capacity (kg) <span style={{ color: DANGER }}>*</span>
                </label>
                <input type="number" value={capacityKg} onChange={(e) => setCapacityKg(e.target.value)}
                  placeholder="e.g. 50" min="0" step="0.5"
                  style={MODAL_INPUT} onFocus={focusAccent} onBlur={blurBorder}
                />
                {editItem && editItem.reservedGrams > 0 && (
                  <div style={{ fontSize: 11, color: WARN, marginTop: 4 }}>
                    {toKg(editItem.reservedGrams)} already reserved by orders
                  </div>
                )}
              </div>
              <div className="col-6">
                <label style={MODAL_LABEL}>
                  Milling Lead (min) <span style={{ color: DANGER }}>*</span>
                </label>
                <input type="number" value={millingLeadMinutes} onChange={(e) => setMillingLeadMinutes(e.target.value)}
                  placeholder="e.g. 90" min="0" step="5"
                  style={MODAL_INPUT} onFocus={focusAccent} onBlur={blurBorder}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={closeEditModal} style={{ padding: "11px 24px", borderRadius: 10, border: "1.5px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text-soft)", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={() => handleDelete(editItem.id)}
                style={{ padding: "11px 20px", borderRadius: 10, border: `1.5px solid ${DANGER_EDGE}`, background: DANGER_WASH, color: DANGER, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                Delete
              </button>
              <button onClick={handleUpdate} disabled={saving} style={{ flex: 1, padding: "11px 24px", borderRadius: 10, border: "none", background: saving ? "var(--mly-accent)" : "var(--mly-heading)", color: "var(--mly-on-accent)", fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer" }}>
                {saving ? "Saving..." : "Update Slot"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MillingSlots;
